/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : DataTable
 *   Feature      : 197-portal-light-theme-responsive (design blueprint)
 *   Description  : THE canonical data table. Column-config API over the shared
 *                  DualScrollTable + usePagination + PaginationControls, with
 *                  token-driven header/row styling and a built-in empty state.
 *                  Replaces hand-rolled <table className="min-w-full …"> markup
 *                  so every list looks and behaves the same (content sizing,
 *                  synced top+bottom scroll only on overflow, page size 10,
 *                  pagination auto-hidden ≤10 rows).
 *   Blueprint    : docs/design-blueprint.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 * ============================================================================
 */
import React, { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { DualScrollTable, usePagination, PaginationControls } from '../common/TableControls';

export interface DataTableColumn<T> {
  /** Stable key; also the default accessor when `render` is omitted. */
  key: string;
  /** Header cell content. */
  header: React.ReactNode;
  /** Cell renderer; defaults to `String((row as any)[key] ?? '')`. */
  render?: (row: T, index: number) => React.ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Extra classes for body cells in this column. */
  cellClassName?: string;
  /** Extra classes for the header cell. */
  headerClassName?: string;
  /** Fixed width (e.g. '16%', '12rem'). Columns keep this width regardless of the
   *  rows shown, so the layout never shifts between filters/pages. Unspecified
   *  columns share the remaining space equally. */
  width?: string;
  /** Let this column wrap instead of truncating with an ellipsis (default truncate). */
  wrap?: boolean;
  /** Disable click-to-sort (default: any column with a plain-string header is
   *  sortable). Set false for action/icon columns. */
  sortable?: boolean;
  /** Value used for sorting; defaults to `row[key]`. Provide when the displayed
   *  value differs from the sort key (e.g. formatted bytes/dates). */
  sortValue?: (row: T) => string | number | null | undefined;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string | number;
  /** Initial page size (minimum/anchor is 10; pagination hides at ≤10 rows). */
  pageSize?: number;
  emptyMessage?: React.ReactNode;
  onRowClick?: (row: T) => void;
  /** Tighter row padding for dense operational tables. */
  dense?: boolean;
  /** Minimum table width (e.g. '64rem'). For wide tables (many columns): below
   *  this width the table keeps its columns readable and scrolls horizontally
   *  (synced top+bottom) instead of cramming everything into the viewport. */
  minWidth?: string;
  /** Set false to suppress the built-in page-size selector + navigation.
   *  Use this when the parent page manages its own server-side pagination. */
  showPagination?: boolean;
  /** Controlled / server-side sort. When `onSortChange` is provided the table does NOT
   *  sort its `rows` internally — a header click calls `onSortChange(key, nextDir)` and the
   *  header arrows reflect `sortKey`/`sortDir`. Use for server-paginated tables that must
   *  sort the WHOLE matching set (not just the current page). Omit for the default
   *  client-side sort. `sortValue`/`sortable` on columns still apply. */
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
  onSortChange?: (key: string, dir: 'asc' | 'desc') => void;
  className?: string;
}

const alignClass = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

/** Canonical list/table. See the design blueprint before introducing a new one. */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  pageSize = 10,
  emptyMessage = 'No records to display',
  onRowClick,
  dense = false,
  minWidth,
  showPagination = true,
  sortKey,
  sortDir,
  onSortChange,
  className,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);

  // Controlled/server-side sort: the parent owns ordering; the table only reflects it and
  // reports header clicks. Otherwise the internal `sort` state drives a client-side re-sort.
  const controlled = typeof onSortChange === 'function';
  const activeKey = controlled ? sortKey : sort?.key;
  const activeDir: 'asc' | 'desc' | undefined = controlled ? sortDir : sort?.dir;

  // Every column with a plain-string header is sortable with no per-page wiring.
  // Columns with a custom JSX header manage their own sorting; action/icon
  // columns (empty header) and `sortable: false` opt out.
  const canSort = (c: DataTableColumn<T>) =>
    c.sortable !== false && typeof c.header === 'string' && c.header.length > 0;

  const sortedRows = useMemo(() => {
    if (controlled || !sort) return rows; // controlled ⇒ server already ordered `rows`
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const valueOf = (r: T): unknown =>
      col.sortValue ? col.sortValue(r) : (r as Record<string, unknown>)[col.key];
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls/blanks always sort last
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [rows, sort, columns, controlled]);

  // Header click: delegate to the parent in controlled mode, else cycle asc → desc → unsorted.
  const handleHeaderSort = (key: string): void => {
    if (controlled) {
      onSortChange!(key, key === sortKey ? (sortDir === 'asc' ? 'desc' : 'asc') : 'desc');
    } else {
      setSort((s) => (s?.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : null));
    }
  };

  const { page, setPage, pageSize: size, changeSize, total, totalPages, pageItems, start } =
    usePagination(sortedRows, pageSize);

  // 🛑 showPagination={false} means THE CALLER OWNS PAGINATION (server-side), so render
  //    every row it handed us. Slicing here silently truncated server-paged tables to
  //    usePagination's default of 10: the Collection Jobs screen asked the server for 25,
  //    the footer said "Showing 1-25 of 25505", and ten rows appeared.
  //
  //    Passing a bigger `pageSize` does NOT fix it - usePagination takes `initialSize`
  //    and seeds useState with it, so any later change to the prop is ignored. The prop
  //    is honoured on mount and never again.
  //
  //    This is why every server-paged table in the app was showing 10 rows regardless of
  //    what its footer claimed.
  const visibleRows = showPagination ? pageItems : sortedRows;

  if (rows.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-[var(--color-text-secondary)]">
        {emptyMessage}
      </div>
    );
  }

  // Condensed by default (Spec 197 owner feedback): smaller font + tight rows so
  // lists pack more on screen. `dense` tightens further for very wide tables.
  const cellPad = dense ? 'px-1.5 py-0.5' : 'px-2 py-1';

  return (
    <div className={className}>
      <DualScrollTable>
        {/* `data-table` + table-fixed: column widths are content-INDEPENDENT, so the
            layout is identical across filters/pages and never overflows the card.
            The `data-table` class opts out of the global content-sizing rule. */}
        <table className="data-table w-full table-fixed border-collapse text-xs leading-5" style={minWidth ? { minWidth } : undefined}>
          <colgroup>
            {columns.map((c) => (
              <col key={c.key} style={c.width ? { width: c.width } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr className="border-b-2 border-[var(--color-border)] bg-[var(--color-bg-subtle)]">
              {columns.map((c) => {
                const sortable = canSort(c);
                const active = activeKey === c.key;
                const justify =
                  c.align === 'right' ? 'justify-end' : c.align === 'center' ? 'justify-center' : 'justify-start';
                return (
                  <th
                    key={c.key}
                    aria-sort={active ? (activeDir === 'asc' ? 'ascending' : 'descending') : undefined}
                    // Full header on hover so a wrapped/narrow header is always recoverable.
                    title={typeof c.header === 'string' ? c.header : undefined}
                    className={clsx(
                      cellPad,
                      'overflow-hidden align-bottom text-[13px] font-semibold leading-tight text-[var(--color-text-primary)]',
                      alignClass[c.align ?? 'left'],
                      c.headerClassName,
                    )}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => handleHeaderSort(c.key)}
                        className={clsx(
                          'group inline-flex max-w-full min-w-0 select-none items-end gap-1 overflow-hidden text-left transition-colors hover:text-[var(--color-accent)]',
                          justify,
                        )}
                      >
                        <span className="truncate">{c.header}</span>
                        {active ? (
                          activeDir === 'asc' ? (
                            <ChevronUp className="mb-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--color-accent)]" />
                          ) : (
                            <ChevronDown className="mb-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--color-accent)]" />
                          )
                        ) : (
                          <ChevronsUpDown className="mb-0.5 h-3.5 w-3.5 flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-50" />
                        )}
                      </button>
                    ) : (
                      <span className="block truncate">{c.header}</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, i) => (
              <tr
                key={rowKey(row, start + i)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={clsx(
                  'border-b border-[var(--color-border)] text-[var(--color-text-primary)]',
                  onRowClick && 'cursor-pointer hover:bg-[var(--color-bg-subtle)]',
                )}
              >
                {columns.map((c) => {
                  const content = c.render
                    ? c.render(row, start + i)
                    : String((row as Record<string, unknown>)[c.key] ?? '');
                  return (
                    <td
                      key={c.key}
                      title={typeof content === 'string' ? content : undefined}
                      className={clsx(
                        cellPad,
                        'align-middle',
                        c.wrap ? 'break-words' : 'truncate',
                        alignClass[c.align ?? 'left'],
                        c.cellClassName,
                      )}
                    >
                      {content}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </DualScrollTable>
      {showPagination && (
        <PaginationControls
          page={page}
          pageSize={size}
          total={total}
          totalPages={totalPages}
          start={start}
          shown={pageItems.length}
          onPage={setPage}
          onPageSize={changeSize}
        />
      )}
    </div>
  );
}

export default DataTable;
